import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Search, X, User, BookOpen, GraduationCap, ClipboardList, UtensilsCrossed, Heart, FileText, Briefcase, Users, BarChart3, MessageSquare, AlertTriangle, FolderOpen } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All searchable pages/features
const PAGES = [
  { path: '/', en: 'Dashboard', bn: 'ড্যাশবোর্ড', icon: BarChart3, keywords: 'home overview kpi summary dashboard হোম' },
  { path: '/attendance', en: 'Attendance', bn: 'হাজিরা', icon: ClipboardList, keywords: 'mark attendance present absent late approval leaderboard হাজিরা' },
  { path: '/lms', en: 'My Learning / CPD & LMS', bn: 'আমার শিখন / CPD', icon: GraduationCap, keywords: 'courses training cpd lms quiz certificate leaderboard need analysis কোর্স প্রশিক্ষণ' },
  { path: '/resource-hub', en: 'Resource Hub', bn: 'রিসোর্স হাব', icon: FolderOpen, keywords: 'lesson plan worksheet template study material question paper ai generate রিসোর্স পাঠ পরিকল্পনা' },
  { path: '/meals', en: 'Meals', bn: 'খাবার', icon: UtensilsCrossed, keywords: 'breakfast lunch snacks meal photo nutrition menu খাবার' },
  { path: '/health', en: 'Health & Wellness', bn: 'স্বাস্থ্য ও সুস্থতা', icon: Heart, keywords: 'health phr blood pressure mood mental wellness fitness steps checkup telemedicine emergency স্বাস্থ্য' },
  { path: '/communication', en: 'Communication Hub', bn: 'যোগাযোগ কেন্দ্র', icon: MessageSquare, keywords: 'circulars community best practices ptm leaderboard messages forum বিজ্ঞপ্তি যোগাযোগ' },
  { path: '/grades', en: 'Grades & Report Card', bn: 'ফলাফল ও রিপোর্ট কার্ড', icon: FileText, keywords: 'grades marks report card periodic midterm annual practical co-curricular ফলাফল রিপোর্ট কার্ড' },
  { path: '/career', en: 'Career & Conclave', bn: 'ক্যারিয়ার ও কনক্লেভ', icon: Briefcase, keywords: 'career conclave linkedin learning aptitude mapping helpline guidance ক্যারিয়ার কনক্লেভ' },
  { path: '/alumni', en: 'Alumni Network', bn: 'প্রাক্তন শিক্ষার্থী', icon: Users, keywords: 'alumni register payback mentorship sessions প্রাক্তন' },
  { path: '/incidents', en: 'Student Portfolio', bn: 'শিক্ষার্থী পোর্টফোলিও', icon: AlertTriangle, keywords: 'student portfolio medical anecdotal vitals guardian পোর্টফোলিও' },
  { path: '/governance', en: 'Governance', bn: 'প্রশাসন', icon: BarChart3, keywords: 'governance sdg4 alerts alert management national dashboard প্রশাসন' },
  { path: '/integrations', en: 'Government Integrations', bn: 'সরকারি সংযোগ', icon: BarChart3, keywords: 'integrations porichoy banbeis iedcr ibas dghs সরকারি সংযোগ' },
];

export default function GlobalSearch() {
  const { lang } = useApp();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ pages: [], students: [], teachers: [], courses: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus input when opened
  useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);

  // Keyboard shortcut: Ctrl+K or Cmd+K
  useEffect(() => {
    const handler = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setIsOpen(true); } if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults({ pages: [], students: [], teachers: [], courses: [] }); return; }
    const lq = q.toLowerCase();

    // Search pages
    const pageResults = PAGES.filter(p => p.en.toLowerCase().includes(lq) || p.bn.includes(q) || p.keywords.toLowerCase().includes(lq)).slice(0, 5);

    // Search API data in parallel
    setLoading(true);
    try {
      const [studentsRes, teachersRes, coursesRes] = await Promise.all([
        axios.get(`${API}/schools`).then(async (schoolsRes) => {
          if (!schoolsRes.data[0]) return [];
          const res = await axios.get(`${API}/schools/${schoolsRes.data[0].school_id}/students`);
          return res.data.filter(s => (s.name_english || '').toLowerCase().includes(lq) || (s.name_bangla || '').includes(q)).slice(0, 5);
        }).catch(() => []),
        axios.get(`${API}/teachers`).then(res => res.data.filter(t => (t.name_english || '').toLowerCase().includes(lq) || (t.name_bangla || '').includes(q) || (t.subjects || []).some(s => s.toLowerCase().includes(lq))).slice(0, 5)).catch(() => []),
        axios.get(`${API}/lms/courses`).then(res => res.data.filter(c => (c.title_english || '').toLowerCase().includes(lq) || (c.title_bangla || '').includes(q) || (c.subject || '').toLowerCase().includes(lq)).slice(0, 5)).catch(() => []),
      ]);
      setResults({ pages: pageResults, students: studentsRes, teachers: teachersRes, courses: coursesRes });
    } catch (e) {
      setResults({ pages: pageResults, students: [], teachers: [], courses: [] });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const goTo = (path) => { navigate(path); setIsOpen(false); setQuery(''); };
  const hasResults = results.pages.length + results.students.length + results.teachers.length + results.courses.length > 0;

  if (!isOpen) {
    return (
      <button data-testid="global-search-btn" onClick={() => setIsOpen(true)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 'var(--radius-pill)',
          background: 'var(--color-surface-2)', border: '1px solid var(--color-separator)', cursor: 'pointer',
          color: 'var(--color-label-tertiary)', fontSize: 'var(--text-footnote)' }}>
        <Search size={14} />
        <span style={{ minWidth: 60 }}>{lang === 'bn' ? 'অনুসন্ধান' : 'Search'}</span>
        <kbd style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--color-surface-3)', color: 'var(--color-label-tertiary)', border: '1px solid var(--color-separator)' }}>⌘K</kbd>
      </button>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }} data-testid="global-search">
      {/* Search input */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px',
        background: 'var(--color-surface-1)', border: '2px solid var(--color-accent)', borderRadius: 'var(--radius-pill)',
        boxShadow: 'var(--shadow-md)', minWidth: 280 }}>
        <Search size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
        <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder={lang === 'bn' ? 'শিক্ষার্থী, শিক্ষক, কোর্স, পেজ অনুসন্ধান...' : 'Search students, teachers, courses, pages...'}
          style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 'var(--text-subhead)',
            color: 'var(--color-label-primary)', padding: '6px 0' }} />
        {query && <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-label-tertiary)', padding: 2 }}><X size={14} /></button>}
        <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-label-tertiary)', fontSize: 11 }}>Esc</button>
      </div>

      {/* Results dropdown */}
      {query.length >= 2 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 8,
          background: 'var(--color-surface-1)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-xl)',
          border: '1px solid var(--color-separator)', maxHeight: 400, overflowY: 'auto', zIndex: 200 }}>
          
          {loading && <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--color-label-tertiary)', fontSize: 'var(--text-subhead)' }}>{lang === 'bn' ? 'অনুসন্ধান হচ্ছে...' : 'Searching...'}</div>}

          {!loading && !hasResults && (
            <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--color-label-tertiary)' }}>
              <Search size={24} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
              <p style={{ fontSize: 'var(--text-subhead)' }}>{lang === 'bn' ? 'কোনো ফলাফল পাওয়া যায়নি' : `No results for "${query}"`}</p>
            </div>
          )}

          {/* Pages */}
          {results.pages.length > 0 && (
            <div>
              <div style={{ padding: '8px 16px', fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--color-label-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--color-background-raised)' }}>
                {lang === 'bn' ? 'পেজ ও মডিউল' : 'Pages & Modules'}
              </div>
              {results.pages.map(p => { const Icon = p.icon; return (
                <button key={p.path} onClick={() => goTo(p.path)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', width: '100%',
                    border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
                    borderBottom: '1px solid var(--color-separator)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-background-raised)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', background: 'var(--color-accent-light)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={16} style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 500, color: 'var(--color-label-primary)' }}>{lang === 'bn' ? p.bn : p.en}</div>
                    <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>{lang === 'bn' ? p.en : p.bn}</div>
                  </div>
                </button>
              ); })}
            </div>
          )}

          {/* Students */}
          {results.students.length > 0 && (
            <div>
              <div style={{ padding: '8px 16px', fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--color-label-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--color-background-raised)' }}>
                {lang === 'bn' ? 'শিক্ষার্থী' : 'Students'} ({results.students.length})
              </div>
              {results.students.map(s => (
                <button key={s.student_id} onClick={() => goTo('/incidents')}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', width: '100%',
                    border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
                    borderBottom: '1px solid var(--color-separator)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-background-raised)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--color-accent-light)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <User size={14} style={{ color: 'var(--color-accent)' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 500, color: 'var(--color-label-primary)' }}>{lang === 'bn' ? s.name_bangla : (s.name_english || s.name_bangla)}</div>
                    <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>Grade {s.grade} | Roll {s.roll_number} | {lang === 'bn' ? (s.name_english || '') : s.name_bangla}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Teachers */}
          {results.teachers.length > 0 && (
            <div>
              <div style={{ padding: '8px 16px', fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--color-label-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--color-background-raised)' }}>
                {lang === 'bn' ? 'শিক্ষক' : 'Teachers'} ({results.teachers.length})
              </div>
              {results.teachers.map(t => (
                <div key={t.teacher_id}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                    borderBottom: '1px solid var(--color-separator)' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--color-red-light)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <User size={14} style={{ color: 'var(--color-red)' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 500, color: 'var(--color-label-primary)' }}>{lang === 'bn' ? t.name_bangla : (t.name_english || t.name_bangla)}</div>
                    <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>{t.role} | {(t.subjects || []).join(', ')}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Courses */}
          {results.courses.length > 0 && (
            <div>
              <div style={{ padding: '8px 16px', fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--color-label-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', background: 'var(--color-background-raised)' }}>
                {lang === 'bn' ? 'কোর্স' : 'Courses'} ({results.courses.length})
              </div>
              {results.courses.map(c => (
                <button key={c.course_id} onClick={() => goTo('/lms')}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', width: '100%',
                    border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
                    borderBottom: '1px solid var(--color-separator)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-background-raised)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-sm)', background: 'var(--color-warning-bg)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <BookOpen size={14} style={{ color: 'var(--color-warning)' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 'var(--text-subhead)', fontWeight: 500, color: 'var(--color-label-primary)' }}>{c.title_bangla || c.title_english}</div>
                    <div style={{ fontSize: 10, color: 'var(--color-label-tertiary)' }}>{c.subject} | {c.duration_minutes} min | {c.is_mandatory ? 'Mandatory' : 'Optional'}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
